import type { Optional } from '@festina-life/core'
import type {
  editor,
  IMarkdownString,
  IPosition,
  languages,
} from 'monaco-editor'

import {
  type FPlusTextMapper,
  type LanguageWorkerAccessor,
  nullSafeTextMapper,
  type WorkerMapping,
} from '../worker/api'
import type { HoverResult } from '../worker/contracts/HoverResult'

export class FPlusHoverProvider<T> implements languages.HoverProvider {
  private worker: LanguageWorkerAccessor<T>
  private asWorkerInput: WorkerMapping<T>

  constructor(
    worker: LanguageWorkerAccessor<T>,
    asWorkerInput: WorkerMapping<T>,
  ) {
    this.worker = worker
    this.asWorkerInput = asWorkerInput
  }

  provideHover(
    model: editor.ITextModel,
    position: IPosition,
  ): languages.ProviderResult<languages.Hover> {
    return this.hover(model, position)
  }

  private async hover(
    model: editor.ITextModel,
    position: IPosition,
  ): Promise<Optional<languages.Hover>> {
    const worker = await this.worker(model.uri)

    if (!worker) {
      return { contents: [] }
    }

    const input = await this.asWorkerInput({ model, position })

    if (!input?.data) {
      return { contents: [] }
    }

    const result = await worker.hover(input.data)

    if (!result) {
      return null
    }

    return this.resolveHoverItem(result, nullSafeTextMapper(input))
  }

  private resolveHoverItem(
    hovers: HoverResult,
    textMapper: FPlusTextMapper,
  ): languages.Hover {
    return {
      contents: hovers.items.map((item): IMarkdownString => ({
        value: textMapper(item.markdown) ?? '',
        isTrusted: false,
        supportThemeIcons: true,
        supportHtml: false,
      })),
    }
  }
}
